<template>
  <v-dialog v-model="value" persistent scrollable max-width="80vw">
    <v-overlay :value="updating">
      <div class="text-center">
        <v-progress-linear
          color="light-blue"
          height="10"
          :value="progress"
          striped
        ></v-progress-linear>
        <h4 class="mt-4">Uploading image, please wait....</h4>
      </div>
    </v-overlay>
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title>New Artwork</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-2">
        <v-form ref="formbase" v-model="formValid" lazy-validation @submit.prevent>
          <v-row class="py-4">
            <v-col cols="12" class="py-0">
              <v-text-field
                v-model="cardTitle"
                outlined
                clearable
                clear-icon="mdi-close-box"
                dense
                :rules="[rules.required]"
                label="Title"
                placeholder="Enter a title for this request"
                hide-details:
                @blur="isDirty = true"
              >
              </v-text-field>
            </v-col>
            <v-col class="py-0">
              <v-file-input
                label="Image to upload"
                v-model="selectedImage"
                prepend-icon="mdi-image"
                accept="image/jpg,image/jpeg"
                @change="handleImageUpdate"
              ></v-file-input>
            </v-col>
            <v-col cols="12" class="py-0">
              <v-autocomplete
                v-model="selectedImageProducts"
                :items="productTypes"
                outlined
                clearable
                clear-icon="mdi-close-box"
                dense
                multiple
                chips
                hide-details:
                item-text="title"
                label="Products"
                :return-object="true"
                @change="isDirty = true"
              >
                <template v-slot:selection="data">
                  <v-chip
                    class="ma-2"
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    close
                    @click:close="removeProduct(data.item)"
                  >
                    {{ data.item.title }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col
              class="py-0"
              v-for="product of selectedImageProducts"
              :key="product.id"
              cols="12"
            >
              <v-autocomplete
                v-model="product.variants"
                :items="getProductVariants(product.productTypeCode)"
                outlined
                clearable
                clear-icon="mdi-close-box"
                dense
                multiple
                chips
                hide-details:
                item-text="description"
                :label="`Select ${product.title} variants`"
                :return-object="true"
                @change="handleVariantChange"
              >
                <template v-slot:selection="data">
                  <v-chip
                    class="ma-2"
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    close
                    @click:close="removeVariant(product, data.item)"
                  >
                    {{ data.item.description }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-form>
        <v-card v-for="aspect of Object.keys(uniqueAspectRatios)" :key="aspect">
          <v-card-title>Crop for products {{ aspect }} </v-card-title>
          <v-card-text>
            <cropper
              :ref="aspect"
              style="width: 50%"
              :src="artwork.url"
              :debounce="false"
              :stencil-props="{
                aspectRatio: Number(aspect),
              }"
              @change="handleCrop"
            />
          </v-card-text>
        </v-card>
        <pre>{{ uniqueAspectRatios }}</pre>
      </v-card-text>
      <v-card-actions>
        <v-btn
          :disabled="!isDirty || !formValid"
          color="success"
          class="mr-4"
          @click.native="saveArtwork()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import "vue-advanced-cropper/dist/style.css";
import { mapGetters } from "vuex";
import axios from "axios";
import { PNG } from "pngjs";
import decodeTiff from "@/utilities/decode-tiff";
import S3Api from "@/api/s3";
import { CircleStencil, Cropper, Preview } from "vue-advanced-cropper";
import {
  resizeImage,
  readFileAsDataURL,
  getMimeTypeFromFile,
  Uint8ToBase64,
  Tiff2JpegB64,
  blobToDataURL,
  dataURLtoFile,
  fetchBlob,
} from "@/utilities/imageUtilities";

const server = process.env.VUE_APP_API_URL || "";
const api = `${server}/api/artwork`;

export default {
  props: {
    value: Boolean,
  },
  components: {
    Cropper,
    CircleStencil,
    Preview,
  },
  async mounted() {},
  data() {
    return {
      panel: null,
      updating: false,
      currentImage: null,
      progress: 0,
      isDirty: false,
      artwork: null,
      selectedImage: null,
      selectedImageProducts: [],
      cardTitle: null,
      formValid: true,
      products: [],
      productTypes: [],
      rules: {
        imageRequired: () => {
          return !this.artwork.image || "An artwork image is required";
        },
        required: (value) => !!value || "Required.",
      },
    };
  },
  computed: {
    ...mapGetters(["user"]),
    ...mapGetters({ productTypeVariants: "products/productTypeVariants" }),
    uniqueAspectRatios() {
      const ratios = {};
      this.selectedImageProducts.forEach((product) => {
        product.variants?.forEach((variant) => {
          ratios[variant.aspectRatio]
            ? ratios[variant.aspectRatio].push(variant.title)
            : (ratios[variant.aspectRatio] = [variant.title]);
        });
      });
      return ratios;
    },
  },
  async mounted() {},
  async created() {
    this.productTypes = JSON.parse(
      JSON.stringify(this.$store.getters["products/productTypes"])
    );
  },
  methods: {
    getProductVariants(productTypeCode) {
      return this.productTypeVariants.filter(
        (item) => item.productTypeCode === productTypeCode
      );
    },
    removeProduct(item) {
      const index = this.selectedImageProducts.findIndex(
        (v) => v.productTypeCode === item.productTypeCode
      );
      if (index >= 0) this.selectedImageProducts.splice(index, 1);
    },
    removeVariant(product, item) {
      const index = product.variants.findIndex(
        (v) => v.productTypeVariantCode === item.productTypeVariantCode
      );
      if (index >= 0) product.variants.splice(index, 1);
    },
    handleCrop({ coordinates, image }) {
      // console.log({
      //   coordinates,
      //   image,
      // });
    },
    handleVariantChange(evt) {
      // console.log("variant changed", evt);
    },
    async handleImageUpdate() {
      this.artwork = null;
      if (this.selectedImage) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          this.artwork = {
            url: e.target.result,
            contentType: getMimeTypeFromFile(e.target.result, this.selectedImage.type),
          };
        };
        reader.readAsDataURL(this.selectedImage);
      } else {
      }
    },
    async saveArtwork() {
      Object.keys(this.uniqueAspectRatios).forEach(async (aspect) => {
        const { coordinates, canvas } = this.$refs[aspect.toString()][0].getResult();
        canvas.toBlob(async (blob) => {
          const data = {
            path: `artwork-uploads/${this.user.artist.artistCode}`,
            name: `aspect-${aspect}/${this.selectedImage.name}`,
            file: blob,
          };
          const postData = await S3Api.getS3PresignedPost(
            `${data.path}/${data.name}`,
            "S3_CUSTOMER_UPLOADS_BUCKET"
          );
          await axios.put(postData.data, data.file, {
            // onUploadProgress: (progressEvent) => {
            //   imageProgress[index] = progressEvent.loaded;
            //   totalLoaded = imageProgress.reduce((a, b) => a + b, 0);
            //   this.progress = (totalLoaded / totalSize) * 100;
            //   this.currentImage = data.name;
            // },
            headers: {
              "Content-Type": this.artwork.contentType,
            },
          });
        }, this.artwork.contentType);
      });
      // let uploads = [];
      // let totalSize = 0;
      // let totalLoaded = 0;
      // let imageProgress = [];
      // this.updating = true;
      // for (const index in this.filesArray) {
      //   totalSize += this.filesArray[index].file.size;
      //   imageProgress[index] = 0;
      // }
      // for (const index in this.filesArray) {
      //   const image = this.filesArray[index];
      //   const data = {
      //     path: `artwork-uploads/${this.user.artist.artistCode}`,
      //     name: image.file.name,
      //     file: image.file,
      //   };
      //   const postData = await S3Api.getS3PresignedPost(
      //     `${data.path}/${data.name}`,
      //     "S3_CUSTOMER_UPLOADS_BUCKET"
      //   );
      //   const formData = new FormData();
      //   formData.append("file", data.file, data.name);
      //   uploads.push(
      //     axios.put(postData.data, data.file, {
      //       onUploadProgress: (progressEvent) => {
      //         imageProgress[index] = progressEvent.loaded;
      //         totalLoaded = imageProgress.reduce((a, b) => a + b, 0);
      //         this.progress = (totalLoaded / totalSize) * 100;
      //         this.currentImage = data.name;
      //       },
      //       headers: {
      //         "Content-Type": "image/jpeg",
      //       },
      //     })
      //   );
      // }
      // Promise.all(uploads).then(async (result) => {
      //   // const url = await S3Api.getS3PresignedGet(`artwork-uploads/${this.user.artist.artistCode}/mytest.jpg`, "S3_CUSTOMER_UPLOADS_BUCKET")
      //   this.updating = false;
      //   this.$emit("close");
      // });
    },
    close() {
      this.$emit("close");
    },
    log(v) {
      this.isDirty = true;
      this.formValid = this.$refs.formbase.validate();
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep.v-dialog__content {
  align-items: start;
  margin-top: 50px;
}
</style>
